import {
  addDays,
  endOfDay,
  endOfYear,
  startOfDay,
  startOfYear,
  addMonths,
  addYears,
  isSameDay,
  subYears,
} from "date-fns";
import { StaticRange } from "react-date-range";
import { IDateRange } from "../../types/generic";
import { AnalyticsDateShortcut } from "../../types/analytics/AnalyticsSavedFilter";

/** Fresh browser-local boundaries for "today" — call each time, do not cache at module load. */
export function getStaticDateValues(now: Date = new Date()) {
  return {
    endOfToday: endOfDay(now),
    startOf7DaysAgo: startOfDay(addDays(now, -7)),
    startOf30DaysAgo: startOfDay(addDays(now, -30)),
    startOf3MonthsAgo: startOfDay(addMonths(now, -3)),
    startOf6MonthsAgo: startOfDay(addMonths(now, -6)),
    startOf1YearAgo: startOfDay(addYears(now, -1)),
    startOfThisYear: startOfDay(startOfYear(now)),
    startOfLastFullYear: startOfDay(startOfYear(subYears(now, 1))),
    endOfLastFullYear: endOfDay(endOfYear(subYears(now, 1))),
    /** Product "All time" floor - not the dawn of time. */
    startOfAllTime: startOfDay(new Date(2000, 0, 1)),
  };
}

/** @deprecated Prefer getStaticDateValues() so midnight boundaries stay fresh. */
export const staticDateValues = getStaticDateValues();

/** Browser-local absolute range for a Common (or Last-12 fallback) shortcut. */
export function dateRangeForShortcut(
  shortcut: AnalyticsDateShortcut
): IDateRange | null {
  const values = getStaticDateValues();
  const end = values.endOfToday;
  switch (shortcut) {
    case "LAST_30_DAYS":
      return { startDate: values.startOf30DaysAgo, endDate: end };
    case "LAST_3_MONTHS":
      return { startDate: values.startOf3MonthsAgo, endDate: end };
    case "LAST_6_MONTHS":
      return { startDate: values.startOf6MonthsAgo, endDate: end };
    case "LAST_YEAR":
    case "LAST_12_MONTHS":
      return { startDate: values.startOf1YearAgo, endDate: end };
    case "YEAR_TO_DATE":
      return { startDate: values.startOfThisYear, endDate: end };
    case "LAST_FULL_YEAR":
      return {
        startDate: values.startOfLastFullYear,
        endDate: values.endOfLastFullYear,
      };
    case "ALL_TIME":
      return { startDate: values.startOfAllTime, endDate: end };
    default:
      return null;
  }
}

const staticRangeHandler = {
  range: {},
  isSelected(range: { startDate: number | Date; endDate: number | Date }) {
    // @ts-ignore
    const definedRange = this.range();
    return (
      isSameDay(range.startDate, definedRange.startDate) &&
      isSameDay(range.endDate, definedRange.endDate)
    );
  },
};

export function createStaticRanges(ranges: any[]): StaticRange[] {
  return ranges.map((range) => ({ ...staticRangeHandler, ...range }));
}

export const defaultStaticRanges = createStaticRanges([
  {
    label: "Last 7 Days",
    range: () => {
      const values = getStaticDateValues();
      return {
        startDate: values.startOf7DaysAgo,
        endDate: values.endOfToday,
      };
    },
  },
  {
    label: "Last 30 Days",
    range: () => {
      const values = getStaticDateValues();
      return {
        startDate: values.startOf30DaysAgo,
        endDate: values.endOfToday,
      };
    },
  },
  {
    label: "Last 3 Months",
    range: () => {
      const values = getStaticDateValues();
      return {
        startDate: values.startOf3MonthsAgo,
        endDate: values.endOfToday,
      };
    },
  },
  {
    label: "Last 6 Months",
    range: () => {
      const values = getStaticDateValues();
      return {
        startDate: values.startOf6MonthsAgo,
        endDate: values.endOfToday,
      };
    },
  },
  {
    label: "Last Year",
    range: () => {
      const values = getStaticDateValues();
      return {
        startDate: values.startOf1YearAgo,
        endDate: values.endOfToday,
      };
    },
  },
]);
